import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getUserData,
  isUserLoggedIn,
  isUserEmailVerified,
} from "../../utils/authUtils";
import { userProfileService } from "../../api/services/userProfileService";
import EmailVerificationModal from "../EmailVerificationModal/EmailVerificationModal";

/**
 * Shows the email verification modal on every route load / refresh when the
 * user is logged in but has not verified their email. Dismissal hides it until
 * the next navigation, refresh, or auth change.
 */
const EmailVerificationGate = () => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [, setAuthTick] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendFeedback, setResendFeedback] = useState("");

  useEffect(() => {
    setDismissed(false);
    setResendFeedback("");
  }, [location.pathname, location.key]);

  useEffect(() => {
    const onAuthChange = () => {
      setDismissed(false);
      setAuthTick((t) => t + 1);
    };
    window.addEventListener("wireoxAuthChanged", onAuthChange);
    return () => window.removeEventListener("wireoxAuthChanged", onAuthChange);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "userData" || e.key === "userLoggedIn") {
        setDismissed(false);
        setAuthTick((t) => t + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!isUserLoggedIn() || isUserEmailVerified() || dismissed) {
    return null;
  }

  const variant =
    location.pathname === "/checkout" ? "checkout" : "login";

  const handleResendConfirmationEmail = async () => {
    const email = getUserData()?.email?.trim();
    if (!email) {
      setResendFeedback(
        "We could not find your email. Please sign in again.",
      );
      return;
    }
    setResendLoading(true);
    setResendFeedback("");
    try {
      const res = await userProfileService.resendConfirmationEmail(email);
      const ok =
        Number(res?.success) === 1 ||
        res?.success === true ||
        res?.success === "1";
      setResendFeedback(
        ok
          ? (res?.msg ?? "Email sent successfully.")
          : (res?.msg ?? "Unable to send email. Please try again."),
      );
    } catch (err) {
      setResendFeedback(
        err?.response?.data?.msg ??
          err?.response?.data?.message ??
          "Unable to send email. Please try again.",
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <EmailVerificationModal
      variant={variant}
      onClose={() => setDismissed(true)}
      onResend={handleResendConfirmationEmail}
      resendLoading={resendLoading}
      resendFeedback={resendFeedback}
    />
  );
};

export default EmailVerificationGate;
